import { useState, useMemo } from 'react';

// ─── Runtime colour helpers ───────────────────────────────────────────────────
const utilColor = (u) =>
  u >= 75 ? '#f59e0b' : u >= 50 ? '#3b82f6' : u >= 30 ? '#22c55e' : '#2dd4bf';
const fitColor = (s) =>
  s >= 70 ? '#22c55e' : s >= 45 ? '#3b82f6' : s >= 20 ? '#f59e0b' : '#a1a1aa';
const AVC = [
  '#378ADD',
  '#a78bfa',
  '#22c55e',
  '#f59e0b',
  '#ef4444',
  '#2dd4bf',
  '#f97316',
  '#8b5cf6',
  '#ec4899',
  '#84cc16',
  '#06b6d4',
  '#fb923c',
  '#a3e635',
  '#818cf8',
  '#f43f5e',
];

// ─── Resource pool ────────────────────────────────────────────────────────────
const ALL_RESOURCES = [
  {
    id: 'r001',
    name: 'Priya Sharma',
    avatar: 'PS',
    role: 'Senior React Developer',
    experience: 6,
    location: 'Bengaluru',
    timezone: 'IST +5:30',
    utilisation: 74,
    billableUtil: 68,
    weeklyCapacity: 40,
    billedHours: 30,
    availableFrom: 'Immediately',
    lastSignal: '2h ago',
    skills: [
      'React',
      'TypeScript',
      'GraphQL',
      'Node.js',
      'Redux',
      'Next.js',
      'Tailwind',
    ],
    manager: {
      name: 'Rahul Kapoor',
      email: 'rahul.kapoor@company.com',
      role: 'Engineering Manager',
    },
    currentProject: 'Project Orion — Dashboard module',
    notes: 'Available for partial allocation this week',
    pastWork: [
      {
        module: 'OAuth2 login flow',
        project: 'SaaSPlatform',
        tags: ['auth', 'OAuth', 'login', 'JWT', 'security', 'SSO'],
      },
      {
        module: 'Role-based access control UI',
        project: 'AdminPortal',
        tags: ['RBAC', 'roles', 'permissions', 'auth', 'access control'],
      },
      {
        module: 'Invoice data model UI',
        project: 'FinTech CRM',
        tags: ['data model', 'forms', 'invoicing', 'CRUD', 'schema'],
      },
      {
        module: 'Multi-currency display layer',
        project: 'PayPortal v3',
        tags: ['multi-currency', 'formatting', 'invoicing', 'i18n'],
      },
    ],
  },
  {
    id: 'r002',
    name: 'Marcus Webb',
    avatar: 'MW',
    role: 'React Frontend Lead',
    experience: 8,
    location: 'London',
    timezone: 'GMT +1',
    utilisation: 48,
    billableUtil: 41,
    weeklyCapacity: 40,
    billedHours: 16,
    availableFrom: 'Immediately',
    lastSignal: '45m ago',
    skills: [
      'React',
      'TypeScript',
      'Webpack',
      'Testing Library',
      'Storybook',
      'Performance',
    ],
    manager: {
      name: 'Claire Donovan',
      email: 'claire.donovan@company.com',
      role: 'Head of Frontend',
    },
    currentProject: 'Internal tooling (non-billable)',
    notes: 'On bench — actively looking for billable project',
    pastWork: [
      {
        module: 'Session management UI',
        project: 'SecureApp',
        tags: ['session', 'auth', 'security', 'tokens', 'logout'],
      },
      {
        module: 'Multi-factor auth screens',
        project: 'BankingPortal',
        tags: ['MFA', '2FA', 'auth', 'security', 'OTP', 'login'],
      },
      {
        module: 'Component design system',
        project: 'Enterprise CMS',
        tags: ['design system', 'components', 'accessibility'],
      },
    ],
  },
  {
    id: 'r003',
    name: 'Yuna Cho',
    avatar: 'YC',
    role: 'React Native Developer',
    experience: 4,
    location: 'Seoul',
    timezone: 'KST +9',
    utilisation: 62,
    billableUtil: 58,
    weeklyCapacity: 40,
    billedHours: 23,
    availableFrom: 'Next Monday',
    lastSignal: '1h ago',
    skills: [
      'React',
      'React Native',
      'TypeScript',
      'Firebase',
      'Zustand',
      'Expo',
    ],
    manager: {
      name: 'Tom Briggs',
      email: 'tom.briggs@company.com',
      role: 'Delivery Lead',
    },
    currentProject: 'Mobile App v2 — winding down',
    notes: 'Project ending in 5 days — capacity freeing up',
    pastWork: [
      {
        module: 'Biometric auth (Face ID / fingerprint)',
        project: 'FieldSales App',
        tags: ['auth', 'biometric', 'security', 'mobile', 'Face ID'],
      },
      {
        module: 'Push notification system',
        project: 'CRM Mobile',
        tags: ['notifications', 'real-time', 'Firebase', 'mobile'],
      },
    ],
  },
  {
    id: 'r004',
    name: 'Aditya Menon',
    avatar: 'AM',
    role: 'Full Stack Developer',
    experience: 3,
    location: 'Hyderabad',
    timezone: 'IST +5:30',
    utilisation: 35,
    billableUtil: 30,
    weeklyCapacity: 40,
    billedHours: 12,
    availableFrom: 'Immediately',
    lastSignal: '3h ago',
    skills: [
      'React',
      'Node.js',
      'PostgreSQL',
      'REST APIs',
      'Tailwind',
      'Docker',
      'JWT',
    ],
    manager: {
      name: 'Rahul Kapoor',
      email: 'rahul.kapoor@company.com',
      role: 'Engineering Manager',
    },
    currentProject: 'None — between projects',
    notes: 'Available for full allocation',
    pastWork: [
      {
        module: 'JWT auth & token refresh',
        project: 'BillingPro',
        tags: ['auth', 'JWT', 'token', 'refresh', 'security', 'backend'],
      },
      {
        module: 'User auth & RBAC',
        project: 'AdminPortal',
        tags: ['auth', 'RBAC', 'security', 'roles', 'permissions', 'users'],
      },
      {
        module: 'Multi-tenant data model',
        project: 'SaaS Platform',
        tags: ['data model', 'multi-tenant', 'PostgreSQL', 'schema'],
      },
    ],
  },
  {
    id: 'r005',
    name: 'Sophie Renard',
    avatar: 'SR',
    role: 'Senior Frontend Engineer',
    experience: 7,
    location: 'Paris',
    timezone: 'CET +2',
    utilisation: 78,
    billableUtil: 72,
    weeklyCapacity: 40,
    billedHours: 29,
    availableFrom: '20% now, more Jun 15',
    lastSignal: '30m ago',
    skills: ['React', 'TypeScript', 'GraphQL', 'Cypress', 'D3.js', 'Figma'],
    manager: {
      name: 'Claire Donovan',
      email: 'claire.donovan@company.com',
      role: 'Head of Frontend',
    },
    currentProject: 'Analytics platform — maintenance',
    notes: 'Can take 1 day/week now; more from Jun 15',
    pastWork: [
      {
        module: 'Permission settings UI',
        project: 'GlobalPay',
        tags: ['permissions', 'settings', 'UX', 'security', 'roles', 'RBAC'],
      },
      {
        module: 'PDF report generation UI',
        project: 'FinReports',
        tags: ['PDF', 'reporting', 'export', 'print'],
      },
    ],
  },
  {
    id: 'r006',
    name: 'Daniel Osei',
    avatar: 'DO',
    role: 'Full Stack Developer',
    experience: 5,
    location: 'Accra',
    timezone: 'GMT +0',
    utilisation: 55,
    billableUtil: 50,
    weeklyCapacity: 40,
    billedHours: 20,
    availableFrom: 'This week',
    lastSignal: '5h ago',
    skills: [
      'React',
      'TypeScript',
      'Node.js',
      'PostgreSQL',
      'Docker',
      'Redis',
      'OAuth2',
    ],
    manager: {
      name: 'Tom Briggs',
      email: 'tom.briggs@company.com',
      role: 'Delivery Lead',
    },
    currentProject: 'CRM module — Phase 2 complete',
    notes: 'Phase 2 signed off — looking for next assignment',
    pastWork: [
      {
        module: 'OAuth2 & SSO integration',
        project: 'AccountsSync',
        tags: ['OAuth2', 'SSO', 'auth', 'integration', 'security', 'identity'],
      },
      {
        module: 'API key management system',
        project: 'DevPortal',
        tags: ['API keys', 'auth', 'security', 'management', 'access control'],
      },
      {
        module: 'Session & token store (Redis)',
        project: 'SaaSBilling',
        tags: ['session', 'Redis', 'tokens', 'auth', 'security', 'backend'],
      },
      {
        module: 'RBAC — permissions engine',
        project: 'ClientFlow CRM',
        tags: ['RBAC', 'permissions', 'roles', 'auth', 'security', 'access'],
      },
    ],
  },
  {
    id: 'r007',
    name: 'Kenji Watanabe',
    avatar: 'KW',
    role: 'Senior Backend Engineer',
    experience: 9,
    location: 'Tokyo',
    timezone: 'JST +9',
    utilisation: 70,
    billableUtil: 65,
    weeklyCapacity: 40,
    billedHours: 26,
    availableFrom: 'From Jun 10',
    lastSignal: '2h ago',
    skills: [
      'Node.js',
      'Python',
      'AWS',
      'Serverless',
      'GraphQL',
      'PostgreSQL',
      'Redis',
      'Kafka',
      'JWT',
      'OAuth2',
    ],
    manager: {
      name: 'Rahul Kapoor',
      email: 'rahul.kapoor@company.com',
      role: 'Engineering Manager',
    },
    currentProject: 'E-commerce platform — backend complete',
    notes: 'Wrapping up; fully available from Jun 10',
    pastWork: [
      {
        module: 'Auth microservice (OAuth2 + OIDC)',
        project: 'PayX Platform',
        tags: [
          'auth',
          'OAuth2',
          'OIDC',
          'microservice',
          'security',
          'identity',
          'SSO',
        ],
      },
      {
        module: 'MFA & device trust system',
        project: 'FinOps Suite',
        tags: ['MFA', '2FA', 'device trust', 'security', 'auth', 'OTP'],
      },
      {
        module: 'Security audit logging',
        project: 'AccountBridge',
        tags: ['security', 'audit', 'logging', 'compliance', 'auth'],
      },
      {
        module: 'Zero-trust API gateway',
        project: 'StreamOps',
        tags: ['zero-trust', 'API', 'security', 'gateway', 'auth', 'backend'],
      },
    ],
  },
  {
    id: 'r008',
    name: 'Ava Thornton',
    avatar: 'AT',
    role: 'Frontend Developer',
    experience: 2,
    location: 'Dublin',
    timezone: 'IST +1',
    utilisation: 22,
    billableUtil: 18,
    weeklyCapacity: 40,
    billedHours: 7,
    availableFrom: 'Immediately',
    lastSignal: '1h ago',
    skills: ['React', 'JavaScript', 'CSS Modules', 'REST APIs', 'Git', 'Figma'],
    manager: {
      name: 'Claire Donovan',
      email: 'claire.donovan@company.com',
      role: 'Head of Frontend',
    },
    currentProject: 'None',
    notes: 'Recently onboarded — needs a billable project',
    pastWork: [
      {
        module: 'Login & registration screens',
        project: 'DocView Tool',
        tags: ['auth', 'login', 'registration', 'forms', 'UI'],
      },
      {
        module: 'Form builder — CRUD screens',
        project: 'AdminTool',
        tags: ['forms', 'CRUD', 'data entry', 'UI'],
      },
    ],
  },
  {
    id: 'r009',
    name: 'Tariq Hassan',
    avatar: 'TH',
    role: 'QA Lead — Automation',
    experience: 6,
    location: 'Dubai',
    timezone: 'GST +4',
    utilisation: 42,
    billableUtil: 38,
    weeklyCapacity: 40,
    billedHours: 15,
    availableFrom: 'Immediately',
    lastSignal: '3h ago',
    skills: [
      'Selenium',
      'Cypress',
      'Playwright',
      'Jest',
      'Postman',
      'JIRA',
      'Security testing',
    ],
    manager: {
      name: 'Tom Briggs',
      email: 'tom.briggs@company.com',
      role: 'Delivery Lead',
    },
    currentProject: 'Regression suite maintenance',
    notes: 'Available for full allocation',
    pastWork: [
      {
        module: 'Auth & security test suite',
        project: 'FinTech CRM',
        tags: ['auth', 'security', 'testing', 'UAT', 'automation'],
      },
      {
        module: 'RBAC permission test scenarios',
        project: 'PayPortal',
        tags: ['RBAC', 'permissions', 'auth', 'test cases', 'security'],
      },
      {
        module: 'API security testing',
        project: 'BillingPro',
        tags: ['API testing', 'auth', 'security', 'Postman', 'penetration'],
      },
    ],
  },
  {
    id: 'r010',
    name: 'Leila Ahmadi',
    avatar: 'LA',
    role: 'Senior UX Designer',
    experience: 7,
    location: 'Amsterdam',
    timezone: 'CET +2',
    utilisation: 50,
    billableUtil: 45,
    weeklyCapacity: 40,
    billedHours: 18,
    availableFrom: 'This week',
    lastSignal: '4h ago',
    skills: [
      'Figma',
      'UX research',
      'Prototyping',
      'Design systems',
      'Accessibility',
      'User testing',
    ],
    manager: {
      name: 'Claire Donovan',
      email: 'claire.donovan@company.com',
      role: 'Head of Frontend',
    },
    currentProject: 'Design system — v2 tokens',
    notes: 'Can split 50/50 across two projects',
    pastWork: [
      {
        module: 'Auth & login UX flow',
        project: 'FinTech CRM',
        tags: ['auth', 'login', 'UX', 'user flow', 'security'],
      },
      {
        module: 'Permission settings UI',
        project: 'GlobalPay',
        tags: ['permissions', 'settings', 'UX', 'security', 'RBAC'],
      },
    ],
  },
  {
    id: 'r011',
    name: 'James Okafor',
    avatar: 'JO',
    role: 'Business Analyst',
    experience: 5,
    location: 'Lagos',
    timezone: 'WAT +1',
    utilisation: 38,
    billableUtil: 35,
    weeklyCapacity: 40,
    billedHours: 14,
    availableFrom: 'Immediately',
    lastSignal: '6h ago',
    skills: [
      'Requirements analysis',
      'BRD writing',
      'Process mapping',
      'Stakeholder management',
      'Agile',
      'JIRA',
    ],
    manager: {
      name: 'Rahul Kapoor',
      email: 'rahul.kapoor@company.com',
      role: 'Engineering Manager',
    },
    currentProject: 'None — off project',
    notes: 'Available immediately for full allocation',
    pastWork: [
      {
        module: 'Auth & access control requirements',
        project: 'PayPortal v3',
        tags: [
          'auth',
          'access control',
          'requirements',
          'BRD',
          'security',
          'RBAC',
        ],
      },
      {
        module: 'Security compliance mapping',
        project: 'FinTech CRM',
        tags: ['security', 'compliance', 'requirements', 'mapping', 'auth'],
      },
    ],
  },
  {
    id: 'r012',
    name: 'Mei Lin',
    avatar: 'ML',
    role: 'DevOps / Cloud Engineer',
    experience: 6,
    location: 'Singapore',
    timezone: 'SGT +8',
    utilisation: 60,
    billableUtil: 55,
    weeklyCapacity: 40,
    billedHours: 22,
    availableFrom: 'Partial — 2 days/wk',
    lastSignal: '1h ago',
    skills: [
      'AWS',
      'Terraform',
      'Docker',
      'Kubernetes',
      'CI/CD',
      'GitHub Actions',
      'IAM',
      'Secrets Manager',
    ],
    manager: {
      name: 'Tom Briggs',
      email: 'tom.briggs@company.com',
      role: 'Delivery Lead',
    },
    currentProject: 'Cloud migration — phase 3',
    notes: 'Phase 3 automated; 2 days/week available',
    pastWork: [
      {
        module: 'AWS IAM & secrets management',
        project: 'FinOps Suite',
        tags: ['IAM', 'secrets', 'AWS', 'security', 'auth', 'access control'],
      },
      {
        module: 'Certificate & token rotation',
        project: 'GlobalPay',
        tags: ['certificates', 'tokens', 'rotation', 'security', 'auth', 'PKI'],
      },
      {
        module: 'Security hardening — infra',
        project: 'BillingPro',
        tags: ['security', 'hardening', 'infra', 'DevOps', 'compliance'],
      },
    ],
  },
  {
    id: 'r013',
    name: 'Fatima Al-Rashid',
    avatar: 'FA',
    role: 'Backend Developer (Python)',
    experience: 5,
    location: 'Cairo',
    timezone: 'EET +3',
    utilisation: 30,
    billableUtil: 28,
    weeklyCapacity: 40,
    billedHours: 11,
    availableFrom: 'Immediately',
    lastSignal: '4h ago',
    skills: [
      'Python',
      'Django',
      'DRF',
      'PostgreSQL',
      'Celery',
      'Redis',
      'JWT',
      'OAuth2',
      'Security',
    ],
    manager: {
      name: 'Tom Briggs',
      email: 'tom.briggs@company.com',
      role: 'Delivery Lead',
    },
    currentProject: 'None — bench',
    notes: 'Strong auth and security background',
    pastWork: [
      {
        module: 'Auth service — OAuth2 + JWT',
        project: 'SaaSBilling',
        tags: [
          'auth',
          'OAuth2',
          'JWT',
          'backend',
          'security',
          'Django',
          'token',
        ],
      },
      {
        module: 'Password policy & reset flows',
        project: 'WalletX',
        tags: ['auth', 'password', 'security', 'reset', 'policy', 'backend'],
      },
      {
        module: 'Permission middleware',
        project: 'RecurBill',
        tags: [
          'permissions',
          'middleware',
          'auth',
          'security',
          'RBAC',
          'Django',
        ],
      },
      {
        module: 'Audit log service',
        project: 'DocService',
        tags: [
          'audit',
          'logging',
          'security',
          'compliance',
          'backend',
          'trail',
        ],
      },
    ],
  },
  {
    id: 'r014',
    name: 'Arjun Reddy',
    avatar: 'AR',
    role: 'Data Engineer',
    experience: 4,
    location: 'Bengaluru',
    timezone: 'IST +5:30',
    utilisation: 45,
    billableUtil: 40,
    weeklyCapacity: 40,
    billedHours: 16,
    availableFrom: 'Immediately',
    lastSignal: '2h ago',
    skills: [
      'Python',
      'SQL',
      'dbt',
      'Airflow',
      'Tableau',
      'BigQuery',
      'Data modelling',
    ],
    manager: {
      name: 'Rahul Kapoor',
      email: 'rahul.kapoor@company.com',
      role: 'Engineering Manager',
    },
    currentProject: 'Data warehouse migration',
    notes: 'Migration 70% done — partially allocatable',
    pastWork: [
      {
        module: 'Security event monitoring',
        project: 'Analytics SaaS',
        tags: ['security', 'monitoring', 'events', 'analytics', 'logging'],
      },
      {
        module: 'User access analytics pipeline',
        project: 'FinTech CRM',
        tags: ['analytics', 'access', 'reporting', 'pipeline', 'data'],
      },
    ],
  },
  {
    id: 'r015',
    name: 'Chris Nolan',
    avatar: 'CN',
    role: 'Project Manager',
    experience: 8,
    location: 'Manchester',
    timezone: 'GMT +1',
    utilisation: 55,
    billableUtil: 52,
    weeklyCapacity: 40,
    billedHours: 21,
    availableFrom: '50% available now',
    lastSignal: '30m ago',
    skills: [
      'Delivery management',
      'Agile',
      'Risk management',
      'Stakeholder comms',
      'PRINCE2',
      'JIRA',
      'Budgeting',
    ],
    manager: {
      name: 'Claire Donovan',
      email: 'claire.donovan@company.com',
      role: 'Head of Frontend',
    },
    currentProject: 'HealthTech portal — steady state',
    notes: 'In BAU — can split time across two projects',
    pastWork: [
      {
        module: 'Security module delivery',
        project: 'BillingPro',
        tags: [
          'security',
          'delivery',
          'project management',
          'auth',
          'compliance',
        ],
      },
      {
        module: 'Change governance — auth workstream',
        project: 'FinTech CRM',
        tags: ['auth', 'change management', 'governance', 'security'],
      },
    ],
  },
];

// ─── Scoring ──────────────────────────────────────────────────────────────────
function computeSimilarity(resource, ctx) {
  if (!ctx.trim()) return { score: 0, matches: [] };
  const tokens = ctx
    .toLowerCase()
    .split(/[\s,;.\-/()]+/)
    .filter((t) => t.length > 2);
  const matchMap = {};
  resource.pastWork.forEach((pw) => {
    const haystack = [
      ...pw.tags,
      ...pw.module.toLowerCase().split(/\s+/),
      ...pw.project.toLowerCase().split(/\s+/),
    ];
    const hits = [
      ...new Set(
        haystack.filter((h) =>
          tokens.some((t) => h.includes(t) || t.includes(h)),
        ),
      ),
    ];
    if (hits.length)
      matchMap[pw.module] = {
        module: pw.module,
        project: pw.project,
        hits: hits.length,
        tags: hits,
      };
  });
  const matches = Object.values(matchMap).sort((a, b) => b.hits - a.hits);
  const score = Math.min(
    Math.round(
      (matches.reduce((a, m) => a + m.hits, 0) /
        Math.max(tokens.length * 1.6, 1)) *
        100,
    ),
    97,
  );
  return { score, matches };
}

function skillMatch(resource, required) {
  if (!required.length) return { matched: [], score: 0 };
  const matched = required.filter((s) =>
    resource.skills.some((rs) => rs.toLowerCase().includes(s.toLowerCase())),
  );
  return {
    matched,
    score: Math.round((matched.length / required.length) * 100),
  };
}

function fitScore(resource, taskSim, skillSim, weights) {
  const total =
    weights.pastWork +
    weights.skills +
    weights.availability +
    weights.experience;
  return Math.round(
    ((taskSim.score / 100) * (weights.pastWork / total) +
      (skillSim.score / 100) * (weights.skills / total) +
      ((100 - resource.utilisation) / 100) * (weights.availability / total) +
      Math.min(resource.experience / 10, 1) * (weights.experience / total)) *
      100,
  );
}

// ─── Ring ─────────────────────────────────────────────────────────────────────
const Ring = ({ value, size = 46, color, label }) => {
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const fill = (value / 100) * circ;
  return (
    <div className="flex flex-shrink-0 flex-col items-center gap-0.5">
      <div style={{ position: 'relative', width: size, height: size }}>
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke="#3f3f46"
            strokeWidth={4}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={color}
            strokeWidth={4}
            strokeDasharray={`${fill} ${circ}`}
            strokeLinecap="round"
            style={{ transition: 'stroke-dasharray 0.45s ease' }}
          />
        </svg>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: "'JetBrains Mono','Fira Code',monospace",
            fontSize: 10,
            fontWeight: 700,
            color,
          }}
        >
          {value}%
        </div>
      </div>
      {label && (
        <div className="max-w-[52px] text-center text-[8px] leading-tight text-zinc-500">
          {label}
        </div>
      )}
    </div>
  );
};

// ─── Avatar ───────────────────────────────────────────────────────────────────
const Avatar = ({ initials, idx, size = 40 }) => (
  <div
    style={{
      width: size,
      height: size,
      borderRadius: size / 2,
      flexShrink: 0,
      background: AVC[idx % AVC.length] + '22',
      border: `1.5px solid ${AVC[idx % AVC.length]}55`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: "'JetBrains Mono','Fira Code',monospace",
      fontSize: size * 0.28,
      fontWeight: 700,
      color: AVC[idx % AVC.length],
    }}
  >
    {initials}
  </div>
);

// ─── Skill chip ───────────────────────────────────────────────────────────────
const Chip = ({ label, matched }) => (
  <span
    className={`rounded-[4px] border-[0.5px] px-[7px] py-[2px] font-mono text-[10px] font-${matched ? 'semibold' : 'normal'}`}
    style={{
      background: matched ? 'rgba(34,197,94,0.12)' : '#18181b',
      color: matched ? '#22c55e' : '#a1a1aa',
      borderColor: matched ? 'rgba(34,197,94,0.3)' : '#27272a',
      fontFamily: "'JetBrains Mono','Fira Code',monospace",
    }}
  >
    {label}
  </span>
);

// ─── Weight slider ────────────────────────────────────────────────────────────
const WeightSlider = ({ label, value, color, onChange }) => (
  <div>
    <div className="mb-1.5 flex justify-between">
      <span className="font-mono text-[10px] text-zinc-500">{label}</span>
      <span className="font-mono text-[10px] font-semibold" style={{ color }}>
        {value}%
      </span>
    </div>
    <input
      type="range"
      min={0}
      max={60}
      step={5}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="h-[3px] w-full cursor-pointer appearance-none rounded-full"
      style={{
        accentColor: color,
        background: `linear-gradient(to right, ${color} ${(value / 60) * 100}%, #3f3f46 ${(value / 60) * 100}%)`,
      }}
    />
  </div>
);

// ─── Email modal ──────────────────────────────────────────────────────────────
const EmailModal = ({
  resource,
  projectName,
  taskContext,
  onClose,
  onSend,
}) => {
  const [body, setBody] = useState(
    `Hi ${resource.manager.name},\n\nI'm the PM on "${projectName}" and would like to request ${resource.name} (${resource.role}) for allocation.\n\nRequired for: ${taskContext || 'module work — details TBC'}\nCurrent utilisation: ${resource.utilisation}% (${100 - resource.utilisation}% capacity free)\nRequested start: ${resource.availableFrom}\n\nPlease confirm availability and the allocation process.\n\nThanks`,
  );
  const [sent, setSent] = useState(false);
  const go = () => {
    setSent(true);
    setTimeout(() => {
      onSend();
      onClose();
    }, 1100);
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-6"
      style={{ background: 'rgba(0,0,0,0.78)', backdropFilter: 'blur(4px)' }}
      role="button"
      tabIndex={-1}
      onClick={(e) => e.target === e.currentTarget && onClose()}
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
    >
      <div
        className="w-full max-w-[540px] overflow-hidden rounded-2xl"
        style={{
          background: '#18181b',
          border: '1px solid #3f3f46',
          boxShadow: '0 24px 64px rgba(0,0,0,0.7)',
          animation: 'raSlide 0.18s ease-out',
        }}
      >
        <div
          className="flex items-center justify-between px-5 py-[14px]"
          style={{ borderBottom: '1px solid #27272a' }}
        >
          <div>
            <div className="mb-0.5 font-mono text-[9px] text-zinc-500">
              Assignment request
            </div>
            <div className="text-[14px] font-medium text-zinc-200">
              {resource.name} · {resource.role}
            </div>
          </div>
          <button
            onClick={onClose}
            className="cursor-pointer border-none bg-transparent text-lg text-zinc-500 hover:text-zinc-200"
          >
            ✕
          </button>
        </div>
        <div
          className="grid grid-cols-2 gap-2 p-5"
          style={{ borderBottom: '1px solid #27272a' }}
        >
          {[
            { l: 'To', v: resource.manager.email },
            {
              l: 'Manager',
              v: `${resource.manager.name} · ${resource.manager.role}`,
            },
            { l: 'Resource', v: `${resource.name} · ${resource.role}` },
            {
              l: 'Capacity free',
              v: `${100 - resource.utilisation}% (${resource.weeklyCapacity - resource.billedHours}h/wk)`,
            },
          ].map((f) => (
            <div
              key={f.l}
              className="rounded-lg p-2"
              style={{ background: '#27272a' }}
            >
              <div className="mb-0.5 font-mono text-[9px] text-zinc-500">
                {f.l}
              </div>
              <div className="text-[11px] leading-snug text-zinc-200">
                {f.v}
              </div>
            </div>
          ))}
        </div>
        <div
          className="px-5 py-4"
          style={{ borderBottom: '1px solid #27272a' }}
        >
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={7}
            className="w-full resize-none rounded-lg p-[10px] text-[11px] leading-relaxed text-zinc-200"
            style={{
              background: '#27272a',
              border: '1px solid #3f3f46',
              outline: 'none',
              fontFamily: "'JetBrains Mono','Fira Code',monospace",
            }}
          />
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3">
          <button
            onClick={onClose}
            className="cursor-pointer rounded-lg px-4 py-1.5 text-[12px] text-zinc-400 transition-colors hover:text-zinc-200"
            style={{ background: 'transparent', border: '1px solid #3f3f46' }}
          >
            Cancel
          </button>
          <button
            onClick={go}
            disabled={sent}
            className="cursor-pointer rounded-lg px-5 py-1.5 text-[12px] font-medium transition-all"
            style={{
              background: sent
                ? 'rgba(34,197,94,0.18)'
                : 'rgba(59,130,246,0.9)',
              color: sent ? '#22c55e' : '#fff',
              border: 'none',
            }}
          >
            {sent ? '✓ Sent' : '✉ Send request'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Resource card ────────────────────────────────────────────────────────────
const ResourceCard = ({
  resource,
  idx,
  rank,
  projectName,
  taskContext,
  reqSkills,
  weights,
  onEmail,
  sentIds,
}) => {
  const [open, setOpen] = useState(false);
  const isSent = sentIds.has(resource.id);
  const taskSim = useMemo(
    () => computeSimilarity(resource, taskContext),
    [resource, taskContext],
  );
  const skillSim = skillMatch(resource, reqSkills);
  const fit = fitScore(resource, taskSim, skillSim, weights);
  const free = 100 - resource.utilisation;
  const hasCtx = taskContext.trim().length > 0;
  const isTop = rank === 1 && fit >= 50;
  const relevantWork = hasCtx
    ? resource.pastWork.filter((pw) =>
        taskSim.matches.some((m) => m.module === pw.module),
      )
    : resource.pastWork;

  const rankColor =
    rank === 1
      ? '#22c55e'
      : rank === 2
        ? '#3b82f6'
        : rank === 3
          ? '#f59e0b'
          : '#52525b';

  return (
    <div
      className="overflow-hidden rounded-xl transition-all duration-150"
      style={{
        background: '#18181b',
        border: `1px solid ${isTop ? 'rgba(34,197,94,0.35)' : '#27272a'}`,
      }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#3f3f46')}
      onMouseLeave={(e) =>
        (e.currentTarget.style.borderColor = isTop
          ? 'rgba(34,197,94,0.35)'
          : '#27272a')
      }
    >
      {isTop && (
        <div
          className="h-[2px]"
          style={{
            background: 'linear-gradient(90deg,#22c55e,#2dd4bf,#3b82f6)',
          }}
        />
      )}

      <div className="flex items-center gap-3 px-4 py-3">
        <div
          className="w-5 flex-shrink-0 text-center font-mono font-bold"
          style={{ fontSize: rank <= 3 ? 13 : 11, color: rankColor }}
        >
          #{rank}
        </div>
        <Avatar initials={resource.avatar} idx={idx} size={40} />
        <div className="min-w-0 flex-1">
          <div className="mb-0.5 flex flex-wrap items-center gap-2">
            <span className="text-[14px] font-semibold text-zinc-200">
              {resource.name}
            </span>
            <span className="text-[11px] text-zinc-400">{resource.role}</span>
            <span className="font-mono text-[10px] text-zinc-500">
              {resource.experience}y · {resource.location}
            </span>
          </div>
          {hasCtx && taskSim.matches.length > 0 ? (
            <div className="mb-[5px] flex items-center gap-1.5">
              <span className="text-[9px] text-green-500">✓</span>
              <span className="text-[11px] font-medium text-green-500">
                {taskSim.matches[0].module}
              </span>
              <span className="text-[10px] text-zinc-500">
                — {taskSim.matches[0].project}
              </span>
              {taskSim.matches.length > 1 && (
                <span className="text-[10px] text-zinc-500">
                  +{taskSim.matches.length - 1} more
                </span>
              )}
            </div>
          ) : (
            <div className="mb-[5px] truncate text-[11px] text-zinc-500">
              {resource.currentProject}
            </div>
          )}
          <div className="flex flex-wrap gap-1">
            {resource.skills.slice(0, 6).map((s) => (
              <Chip
                key={s}
                label={s}
                matched={reqSkills.some((r) =>
                  s.toLowerCase().includes(r.toLowerCase()),
                )}
              />
            ))}
            {resource.skills.length > 6 && (
              <span className="font-mono text-[10px] text-zinc-500">
                +{resource.skills.length - 6}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-shrink-0 items-start gap-2.5">
          <Ring
            value={resource.utilisation}
            size={46}
            color={utilColor(resource.utilisation)}
            label="utilisation"
          />
          <Ring value={free} size={46} color="#22c55e" label="free cap." />
          {hasCtx && (
            <Ring
              value={taskSim.score}
              size={46}
              color={fitColor(taskSim.score)}
              label="past work"
            />
          )}
          {reqSkills.length > 0 && (
            <Ring
              value={skillSim.score}
              size={46}
              color={fitColor(skillSim.score)}
              label="skill fit"
            />
          )}
          <Ring value={fit} size={50} color={fitColor(fit)} label="best fit" />
        </div>
        <div className="flex min-w-[144px] flex-shrink-0 flex-col items-end gap-1.5">
          <div className="text-right">
            <div className="mb-0.5 font-mono text-[9px] text-zinc-500">
              Reports to
            </div>
            <div className="text-[12px] font-medium text-zinc-200">
              {resource.manager.name}
            </div>
            <div className="text-[10px] text-zinc-400">
              {resource.manager.role}
            </div>
          </div>
          <div
            className="font-mono text-[9px]"
            style={{ color: free >= 30 ? '#22c55e' : '#f59e0b' }}
          >
            ⚡ {resource.availableFrom}
          </div>
          <div className="flex gap-1.5">
            <button
              onClick={() => setOpen((p) => !p)}
              className="cursor-pointer rounded px-2.5 py-1 text-[11px] text-zinc-400 transition-colors hover:text-zinc-200"
              style={{ background: 'transparent', border: '1px solid #3f3f46' }}
            >
              {open ? 'Less' : 'Details'}
            </button>
            <button
              onClick={() => onEmail(resource)}
              disabled={isSent}
              className="cursor-pointer whitespace-nowrap rounded px-3.5 py-1 text-[11px] font-medium transition-all"
              style={{
                background: isSent
                  ? 'rgba(34,197,94,0.14)'
                  : 'rgba(59,130,246,0.88)',
                color: isSent ? '#22c55e' : '#fff',
                border: 'none',
                cursor: isSent ? 'default' : 'pointer',
              }}
            >
              {isSent ? '✓ Requested' : '✉ Request'}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div
          className="grid grid-cols-2 gap-2.5 px-4 pb-4 pt-3"
          style={{ borderTop: '1px solid #27272a' }}
        >
          <div className="rounded-xl p-3" style={{ background: '#27272a' }}>
            <div className="mb-2 font-mono text-[9px] text-zinc-500">
              {hasCtx
                ? `Relevant past work — ${relevantWork.length > 0 ? `${relevantWork.length} matching module${relevantWork.length > 1 ? 's' : ''}` : 'no direct matches'}`
                : 'Past work history'}
            </div>
            {relevantWork.length === 0 && hasCtx && (
              <div className="text-[11px] leading-relaxed text-zinc-500">
                No past work directly matches the task description. Score is
                based on skills and availability.
              </div>
            )}
            {(hasCtx ? relevantWork : resource.pastWork).map((pw, i) => {
              const m = taskSim.matches.find((x) => x.module === pw.module);
              return (
                <div
                  key={i}
                  className="mb-1.5 rounded-lg px-2.5 py-2 last:mb-0"
                  style={{
                    background: m
                      ? 'rgba(34,197,94,0.07)'
                      : 'rgba(255,255,255,0.02)',
                    border: `0.5px solid ${m ? 'rgba(34,197,94,0.18)' : '#27272a'}`,
                  }}
                >
                  <div className="mb-1 flex items-baseline justify-between gap-2">
                    <div
                      className="text-[12px] font-medium leading-snug"
                      style={{ color: m ? '#22c55e' : '#f4f4f5' }}
                    >
                      {m && <span className="mr-1 text-[9px]">✓</span>}
                      {pw.module}
                    </div>
                    <div className="flex-shrink-0 text-[10px] text-zinc-500">
                      {pw.project}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {pw.tags.slice(0, 5).map((tag) => (
                      <span
                        key={tag}
                        className="rounded-[3px] px-[6px] py-[1px] font-mono text-[9px]"
                        style={{
                          background:
                            m && m.tags.includes(tag)
                              ? 'rgba(34,197,94,0.12)'
                              : '#18181b',
                          color:
                            m && m.tags.includes(tag) ? '#22c55e' : '#71717a',
                          border: `0.5px solid ${m && m.tags.includes(tag) ? 'rgba(34,197,94,0.3)' : '#27272a'}`,
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
            {hasCtx && resource.pastWork.length > relevantWork.length && (
              <div className="mt-1.5 text-[10px] text-zinc-500">
                +{resource.pastWork.length - relevantWork.length} other module
                {resource.pastWork.length - relevantWork.length > 1
                  ? 's'
                  : ''}{' '}
                not relevant to this task
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2.5">
            <div className="rounded-xl p-3" style={{ background: '#27272a' }}>
              <div className="mb-2 font-mono text-[9px] text-zinc-500">
                Capacity & availability
              </div>
              {[
                {
                  l: 'Current project',
                  v: resource.currentProject,
                  c: '#a1a1aa',
                },
                {
                  l: 'Billed hrs/wk',
                  v: `${resource.billedHours}h / ${resource.weeklyCapacity}h`,
                  c: utilColor(resource.utilisation),
                },
                {
                  l: 'Billable util.',
                  v: `${resource.billableUtil}%`,
                  c: '#3b82f6',
                },
                {
                  l: 'Free hrs/wk',
                  v: `${resource.weeklyCapacity - resource.billedHours}h`,
                  c: '#22c55e',
                },
              ].map((row) => (
                <div
                  key={row.l}
                  className="mb-1 flex items-baseline justify-between text-[11px] last:mb-0"
                >
                  <span className="text-zinc-500">{row.l}</span>
                  <span
                    className="font-mono font-medium"
                    style={{ color: row.c }}
                  >
                    {row.v}
                  </span>
                </div>
              ))}
              <div
                className="mt-2 pt-2 text-[10px] leading-relaxed text-zinc-500"
                style={{ borderTop: '0.5px solid #27272a' }}
              >
                {resource.notes}
              </div>
            </div>
            {reqSkills.length > 0 && (
              <div className="rounded-xl p-3" style={{ background: '#27272a' }}>
                <div className="mb-2 font-mono text-[9px] text-zinc-500">
                  Required skill match ({skillSim.score}%)
                </div>
                {reqSkills.map((s) => {
                  const has = resource.skills.some((rs) =>
                    rs.toLowerCase().includes(s.toLowerCase()),
                  );
                  const exact = resource.skills.find((rs) =>
                    rs.toLowerCase().includes(s.toLowerCase()),
                  );
                  return (
                    <div
                      key={s}
                      className="mb-1.5 flex items-center gap-2 last:mb-0"
                    >
                      <div
                        className="flex h-[15px] w-[15px] flex-shrink-0 items-center justify-center rounded-full text-[9px] font-bold"
                        style={{
                          background: has
                            ? 'rgba(34,197,94,0.15)'
                            : 'rgba(239,68,68,0.12)',
                          color: has ? '#22c55e' : '#ef4444',
                        }}
                      >
                        {has ? '✓' : '✗'}
                      </div>
                      <span className="text-[11px] text-zinc-200">{s}</span>
                      {exact && exact !== s && (
                        <span className="text-[10px] text-zinc-500">
                          → {exact}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Panel (inner content) ────────────────────────────────────────────────────
function ResourceAllocationPanel({
  moduleName = 'Module 2 — Auth & Security',
  moduleContext = 'Authentication and security module: OAuth2 / OIDC integration, JWT token management, RBAC permissions engine, MFA, session management, security audit logging, API key management',
  projectName = 'Client Alpha Portal',
  initialSkills = 'OAuth2, JWT, RBAC, Security',
  initialUtilMax = 80,
}) {
  const [taskCtx, setTaskCtx] = useState(moduleContext);
  const [reqInput, setReqInput] = useState(initialSkills);
  const [utilMax, setUtilMax] = useState(initialUtilMax);
  const [expMin, setExpMin] = useState(0);
  const [sortKey, setSortKey] = useState('fit');
  const [projName, setProjName] = useState(projectName);
  const [showWeights, setShowWeights] = useState(false);
  const [weights, setWeights] = useState({
    pastWork: 45,
    skills: 25,
    availability: 20,
    experience: 10,
  });
  const [modalRes, setModalRes] = useState(null);
  const [sentIds, setSentIds] = useState(new Set());

  const reqSkills = reqInput
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const hasCtx = taskCtx.trim().length > 0;

  const results = useMemo(() => {
    return ALL_RESOURCES.filter(
      (r) => r.utilisation <= utilMax && r.experience >= expMin,
    )
      .map((r) => {
        const ts = computeSimilarity(r, taskCtx);
        const ss = skillMatch(r, reqSkills);
        const ft = fitScore(r, ts, ss, weights);
        return { ...r, _ts: ts, _ss: ss, _ft: ft };
      })
      .sort((a, b) => {
        if (sortKey === 'fit') return b._ft - a._ft;
        if (sortKey === 'util_desc') return b.utilisation - a.utilisation;
        if (sortKey === 'util_asc') return a.utilisation - b.utilisation;
        if (sortKey === 'free_desc') return a.utilisation - b.utilisation;
        if (sortKey === 'exp_desc') return b.experience - a.experience;
        return 0;
      });
  }, [taskCtx, reqSkills, utilMax, expMin, sortKey, weights]);

  const stats = useMemo(
    () => ({
      count: results.length,
      freeHrs: results.reduce(
        (a, r) =>
          a + Math.round(((100 - r.utilisation) / 100) * r.weeklyCapacity),
        0,
      ),
      topFit: results[0]?._ft || 0,
      avgUtil: results.length
        ? Math.round(
            results.reduce((a, r) => a + r.utilisation, 0) / results.length,
          )
        : 0,
    }),
    [results],
  );

  const setW = (key) => (val) => setWeights((p) => ({ ...p, [key]: val }));
  const inputCls =
    'w-full rounded-lg text-[11px] text-zinc-200 px-[10px] py-[7px] font-mono focus:outline-none transition-colors';
  const inputStyle = { background: '#27272a', border: '1px solid #3f3f46' };

  return (
    <div className="pb-12 font-sans" style={{ color: '#f4f4f5' }}>
      <div className="mx-auto max-w-[1180px] px-6 pt-5">
        {/* Agent banner */}
        <div
          className="mb-4 flex items-start gap-3 rounded-xl px-4 py-3"
          style={{
            background: 'rgba(59,130,246,0.06)',
            border: '1px solid rgba(59,130,246,0.2)',
          }}
        >
          <div
            className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-sm"
            style={{ background: 'rgba(59,130,246,0.15)' }}
          >
            🤖
          </div>
          <div>
            <div className="mb-0.5 text-[11px] font-medium text-blue-400">
              Agent resolved · {moduleName}
            </div>
            <div className="text-[11px] leading-relaxed text-zinc-400">
              Context graph lookup complete. Module requirements extracted:{' '}
              <span className="text-zinc-200">{initialSkills}</span>. Showing
              resources ranked by past work similarity and skill match. Edit the
              task description or skills below to refine.
            </div>
          </div>
        </div>

        {/* Stats bar */}
        <div className="mb-4 grid grid-cols-4 gap-2">
          {[
            { l: 'Resources found', v: stats.count, c: '#3b82f6' },
            {
              l: 'Avg utilisation',
              v: stats.avgUtil + '%',
              c: utilColor(stats.avgUtil),
            },
            { l: 'Total free cap.', v: stats.freeHrs + 'h/wk', c: '#22c55e' },
            {
              l: 'Top fit score',
              v: stats.topFit + '%',
              c: fitColor(stats.topFit),
            },
          ].map((s) => (
            <div
              key={s.l}
              className="rounded-xl px-3.5 py-2.5"
              style={{ background: '#18181b', border: '1px solid #27272a' }}
            >
              <div className="mb-0.5 font-mono text-[9px] text-zinc-500">
                {s.l}
              </div>
              <div
                className="font-mono text-[18px] font-bold"
                style={{ color: s.c }}
              >
                {s.v}
              </div>
            </div>
          ))}
        </div>

        {/* Filters panel */}
        <div
          className="mb-3.5 rounded-xl p-4"
          style={{ background: '#18181b', border: '1px solid #27272a' }}
        >
          <div className="mb-3.5 grid grid-cols-[1fr_220px] gap-4">
            <div>
              <div className="mb-1.5 flex items-center gap-2">
                <span
                  className="font-mono text-[9px]"
                  style={{ color: hasCtx ? '#3b82f6' : '#a1a1aa' }}
                >
                  Task / module description
                </span>
                {hasCtx && (
                  <span className="font-mono text-[9px] text-green-500">
                    ✓ past-work similarity active
                  </span>
                )}
              </div>
              <textarea
                value={taskCtx}
                onChange={(e) => setTaskCtx(e.target.value)}
                rows={2}
                className="w-full resize-none rounded-lg px-3 py-2 text-[12px] leading-relaxed text-zinc-200 focus:outline-none"
                style={{
                  background: '#27272a',
                  border: '1px solid #3f3f46',
                  fontFamily: 'inherit',
                }}
              />
            </div>
            <div>
              <div className="mb-1.5 font-mono text-[9px] text-zinc-500">
                Required skills
              </div>
              <input
                value={reqInput}
                onChange={(e) => setReqInput(e.target.value)}
                placeholder="OAuth2, JWT, RBAC…"
                className={inputCls}
                style={inputStyle}
              />
              <div className="mb-1.5 mt-3 font-mono text-[9px] text-zinc-500">
                Your project
              </div>
              <input
                value={projName}
                onChange={(e) => setProjName(e.target.value)}
                className={inputCls}
                style={inputStyle}
              />
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <div className="mb-1.5 font-mono text-[9px] text-zinc-500">
                Max utilisation:{' '}
                <span
                  className="font-semibold"
                  style={{ color: utilColor(utilMax) }}
                >
                  {utilMax}%
                </span>
              </div>
              <div className="flex gap-1.5">
                {[50, 65, 80].map((v) => (
                  <button
                    key={v}
                    onClick={() => setUtilMax(v)}
                    className="cursor-pointer rounded-lg px-3 py-1.5 font-mono text-[11px] font-bold transition-all"
                    style={{
                      background:
                        utilMax === v ? utilColor(v) + '18' : 'transparent',
                      border: `1px solid ${utilMax === v ? utilColor(v) : '#3f3f46'}`,
                      color: utilMax === v ? utilColor(v) : '#a1a1aa',
                    }}
                  >
                    ≤{v}%
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="mb-1.5 font-mono text-[9px] text-zinc-500">
                Min experience
              </div>
              <select
                value={expMin}
                onChange={(e) => setExpMin(Number(e.target.value))}
                className="cursor-pointer rounded-lg px-2.5 py-1.5 font-mono text-[11px] text-zinc-200 focus:outline-none"
                style={{ background: '#27272a', border: '1px solid #3f3f46' }}
              >
                {[0, 2, 3, 5, 7].map((v) => (
                  <option key={v} value={v}>
                    {v === 0 ? 'Any level' : `${v}+ yrs`}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <div className="mb-1.5 font-mono text-[9px] text-zinc-500">
                Sort by
              </div>
              <select
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value)}
                className="cursor-pointer rounded-lg px-2.5 py-1.5 font-mono text-[11px] text-zinc-200 focus:outline-none"
                style={{ background: '#27272a', border: '1px solid #3f3f46' }}
              >
                <option value="fit">Best fit score</option>
                <option value="util_desc">Highest utilisation first</option>
                <option value="util_asc">Lowest utilisation first</option>
                <option value="free_desc">Most available first</option>
                <option value="exp_desc">Most experienced first</option>
              </select>
            </div>
            <button
              onClick={() => setShowWeights((p) => !p)}
              className="cursor-pointer rounded-lg px-3 py-1.5 font-mono text-[11px] transition-all"
              style={{
                background: showWeights
                  ? 'rgba(139,92,246,0.1)'
                  : 'transparent',
                border: `1px solid ${showWeights ? 'rgba(139,92,246,0.5)' : '#3f3f46'}`,
                color: showWeights ? '#a78bfa' : '#a1a1aa',
              }}
            >
              ⚖ Fit weights
            </button>
          </div>
          {showWeights && (
            <div
              className="mt-3.5 grid grid-cols-4 gap-4 pt-3.5"
              style={{ borderTop: '0.5px solid #27272a' }}
            >
              <WeightSlider
                label="Past work match"
                value={weights.pastWork}
                color="#22c55e"
                onChange={setW('pastWork')}
              />
              <WeightSlider
                label="Skills"
                value={weights.skills}
                color="#3b82f6"
                onChange={setW('skills')}
              />
              <WeightSlider
                label="Availability"
                value={weights.availability}
                color="#f59e0b"
                onChange={setW('availability')}
              />
              <WeightSlider
                label="Experience"
                value={weights.experience}
                color="#a78bfa"
                onChange={setW('experience')}
              />
            </div>
          )}
        </div>

        {/* Results label */}
        <div className="mb-2.5 text-[11px] text-zinc-500">
          <span className="font-medium text-zinc-200">
            {results.length} people
          </span>
          {' · '}utilisation ≤ {utilMax}%
          {reqSkills.length > 0 && (
            <>
              {' '}
              · skills:{' '}
              <span className="text-green-500">{reqSkills.join(', ')}</span>
            </>
          )}
          {hasCtx && (
            <>
              {' '}
              ·{' '}
              <span className="text-blue-400">past-work similarity active</span>
            </>
          )}
          {' · '}sorted by{' '}
          {sortKey === 'fit'
            ? 'best fit'
            : sortKey === 'util_desc'
              ? 'highest utilisation'
              : sortKey === 'util_asc'
                ? 'lowest utilisation'
                : sortKey === 'free_desc'
                  ? 'most available'
                  : 'experience'}
        </div>

        {/* Card list */}
        <div className="flex flex-col gap-2">
          {results.length === 0 ? (
            <div
              className="rounded-xl p-10 text-center text-[13px] text-zinc-500"
              style={{ background: '#18181b', border: '1px solid #27272a' }}
            >
              No resources match the current filters. Try raising the
              utilisation threshold or adjusting skill requirements.
            </div>
          ) : (
            results.map((r, i) => (
              <ResourceCard
                key={r.id}
                resource={r}
                idx={i}
                rank={i + 1}
                projectName={projName}
                taskContext={taskCtx}
                reqSkills={reqSkills}
                weights={weights}
                onEmail={setModalRes}
                sentIds={sentIds}
              />
            ))
          )}
        </div>

        {sentIds.size > 0 && (
          <div
            className="mt-3 rounded-xl px-4 py-3 text-[12px] text-green-500"
            style={{
              background: 'rgba(34,197,94,0.07)',
              border: '1px solid rgba(34,197,94,0.2)',
            }}
          >
            ✓ {sentIds.size} assignment request{sentIds.size > 1 ? 's' : ''}{' '}
            sent to resource managers · awaiting confirmation
          </div>
        )}
      </div>

      {modalRes && (
        <EmailModal
          resource={modalRes}
          projectName={projName}
          taskContext={taskCtx}
          onClose={() => setModalRes(null)}
          onSend={() => setSentIds((p) => new Set([...p, modalRes.id]))}
        />
      )}
    </div>
  );
}

// ─── Modal overlay wrapper ────────────────────────────────────────────────────
export default function ResourceAllocationModal({ data, onClose }) {
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(0,0,0,0.75)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        fontFamily: "'DM Sans','Outfit',system-ui,sans-serif",
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <style>{`
        @keyframes raSlide { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        .ra-scroll::-webkit-scrollbar { width: 4px; }
        .ra-scroll::-webkit-scrollbar-track { background: transparent; }
        .ra-scroll::-webkit-scrollbar-thumb { background: #3f3f46; border-radius: 4px; }
        .ra-scroll select option { background: #18181b; }
      `}</style>

      <div
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          background: '#0e0e0e',
          border: '1px solid #27272a',
          borderRadius: 16,
          overflow: 'hidden',
          width: '92vw',
          maxWidth: 1200,
          height: '90vh',
          animation: 'raSlide 0.2s ease-out',
        }}
      >
        {/* Top bar */}
        <div
          style={{
            background: '#18181b',
            borderBottom: '1px solid #27272a',
            padding: '10px 20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontFamily: 'monospace',
              fontSize: 11,
            }}
          >
            <span style={{ color: '#3b82f6' }}>SuperManager</span>
            <span style={{ color: '#52525b' }}>›</span>
            <span style={{ color: '#a1a1aa' }}>Resource allocation</span>
            <span style={{ color: '#52525b' }}>›</span>
            <span
              style={{
                color: '#f59e0b',
                maxWidth: 180,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {data?.projectName || 'Project'}
            </span>
            <span style={{ color: '#52525b' }}>›</span>
            <span
              style={{
                color: '#a78bfa',
                maxWidth: 200,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {data?.moduleName || 'Module'}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 3,
                  background: '#22c55e',
                  boxShadow: '0 0 5px #22c55e',
                }}
              />
              <span
                style={{
                  fontSize: 10,
                  color: '#a1a1aa',
                  fontFamily: 'monospace',
                }}
              >
                Context graph live
              </span>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'transparent',
                border: '1px solid #3f3f46',
                borderRadius: 6,
                color: '#a1a1aa',
                cursor: 'pointer',
                padding: '4px 10px',
                fontSize: 13,
                lineHeight: 1,
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Scrollable content */}
        <div className="ra-scroll" style={{ flex: 1, overflowY: 'auto' }}>
          <ResourceAllocationPanel
            moduleName={data?.moduleName}
            moduleContext={data?.moduleContext}
            projectName={data?.projectName}
            initialSkills={data?.initialSkills}
          />
        </div>
      </div>
    </div>
  );
}
